/* The customer app, in the artboard's light register.
 *
 * SOURCE: `Damga Customer App.dc.html` (SHA-256 ff5dbf63…7e5c, 17 September).
 * Every value below is the artboard's own unless a comment says otherwise, and
 * the departures are few and named:
 *
 *   TYPE FLOOR. The artboard sets its mono labels at 8 and 9px. On a phone held
 *   at a counter those are unreadable. The floor was 10px until the review of
 *   18 September (findings 26-30) looked at it on a phone: now UPPERCASE mono
 *   labels are 11px and words in sentence case are 12px, and form inputs are
 *   16px because iOS zooms the page into anything smaller.
 *   TARGETS. Every control is 44px in its smaller dimension, or its visible
 *   box sits inside a 44px one (the review found 35 and 40px controls).
 *   LEFTOVER CLAY. A handful of the artboard's values are the old register
 *   painted over: an amber inset on the code cells, a brown ink on the reward
 *   dot, dark ink on the dark seal. Each is replaced with the teal family and
 *   marked where it sits.
 *   RULES YOU CAN SEE. The artboard draws the "or" rule and the sheet grabber at
 *   .05 and .07 alpha, which do not render on most screens. Both are .14.
 *
 * ONE THEME, deliberately. The artboard is a light wallet and the product is
 * the card in it; `color-scheme: light` says so to the browser rather than
 * letting a dark phone half-invert the form controls.
 *
 * A CARD'S HUE IS A CUSTOM PROPERTY, set per card by the component, and the
 * artboard's two card functions are written against it here:
 *   grad(hue)  linear-gradient(155deg, hue 10% into white, hue 20% into white)
 *   clay(hue)  a soft drop plus a 1px inner line of hue 22% into white
 */
export const appCss = `
@font-face {
  font-family: "Bricolage Grotesque";
  src: url("/fonts/BricolageGrotesque-variable-latin.woff2") format("woff2");
  font-weight: 400 800;
  font-display: swap;
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
@font-face {
  font-family: "Schibsted Grotesk";
  src: url("/fonts/SchibstedGrotesk-variable-latin.woff2") format("woff2");
  font-weight: 400 900;
  font-display: swap;
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
/* One face for 400 to 600: the artboard asks for 500 and 600, and Google's own
   stylesheet served both from this file. A range stops the browser faking a
   bold on top of it. */
@font-face {
  font-family: "DM Mono";
  src: url("/fonts/DMMono-500-latin.woff2") format("woff2");
  font-weight: 400 600;
  font-display: swap;
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}

:root {
  color-scheme: light;
  --font-sans: "Schibsted Grotesk", system-ui, -apple-system, sans-serif;
  --font-mono: "DM Mono", ui-monospace, SFMono-Regular, monospace;
  --font-display: "Bricolage Grotesque", "Schibsted Grotesk", sans-serif;

  /* The artboard's names are the clay register's (--cream is its ink, --ochre
     its accent). The roles are what they mean now. */
  --ink: #08282f;
  --ink-deep: #04282e;
  --accent: #0b5c74;
  --seal: #0e7490;
  --sky: #38bdf8;
  --fill: #5eead4;
  --ok: #0c6543;
  --danger: #993524;

  --ink-88: rgba(8, 40, 47, 0.88);
  --ink-86: rgba(8, 40, 47, 0.86);
  --ink-78: rgba(8, 40, 47, 0.78);
  --ink-76: rgba(8, 40, 47, 0.76);
  --ink-74: rgba(8, 40, 47, 0.74);
  --tint: rgba(8, 40, 47, 0.06);
  --tint-strong: rgba(8, 40, 47, 0.07);
  --line: rgba(8, 40, 47, 0.14);
  --line-strong: rgba(8, 40, 47, 0.16);
  --line-input: rgba(8, 40, 47, 0.26);

  --ground: #eef4f5;
  --page: linear-gradient(172deg, #ffffff, #f7fafb 54%, #eef4f5);
  --shell: linear-gradient(155deg, #ffffff, #f4f9fa);
  --shell-shadow: 0 18px 38px -26px rgba(8, 40, 47, 0.3), inset 0 0 0 1px rgba(8, 40, 47, 0.1);
  --grad-accent: linear-gradient(160deg, #5eead4, #4dd6e8 55%, #38bdf8);
  --cta-shadow: 0 6px 14px -9px rgba(8, 40, 47, 0.34), inset 0 0 0 1px color-mix(in oklch, #0b5c74 22%, #ffffff);

  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
  --ease-drawer: cubic-bezier(0.32, 0.72, 0, 1);
}

*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; background: var(--ground); }
body {
  font-family: var(--font-sans);
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  -webkit-tap-highlight-color: transparent;
  text-rendering: optimizeLegibility;
}
h1, h2, h3 { font-optical-sizing: auto; }
button { font: inherit; color: inherit; }
:focus-visible { outline: 2px solid var(--seal); outline-offset: 2px; }
p { margin: 0; }

/* --- the column --------------------------------------------------------------
   A phone is the product. On a tablet or a desktop the column stays a phone's
   width and sits on the ground as a panel, rather than stretching into a
   ribbon of cards across an empty screen. */
.app {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  max-width: 440px;
  margin: 0 auto;
  background: var(--page);
}
@media (min-width: 640px) {
  body { padding: 28px 0; }
  .app {
    min-height: calc(100dvh - 56px);
    border-radius: 30px;
    box-shadow: 0 40px 80px -40px rgba(8, 40, 47, 0.35), inset 0 0 0 1px rgba(8, 40, 47, 0.08);
  }
  .tabs { border-radius: 24px; margin: 0 8px 8px; }
}

/* --- a wide screen: a tablet, a laptop (review finding 25, 2026-09-19) ------------
   The column above stays a phone's on purpose: stretched, the cards become a ribbon
   across an empty screen. But a phone column alone in a 1440 field wastes the one
   advantage a large screen has. So the signed-in app uses the width by composing,
   not stretching: Home's code and its facts stay in view in one column while what
   there is to do fills the other; card lists go as many across as a card's own
   width allows; the tab bar keeps a phone's proportions; reading screens keep a
   reading width. Signing in stays a phone's column: a form needs nothing more. */
@media (min-width: 760px) {
  .app-shell { max-width: 1040px; width: calc(100% - 48px); }
  .app-shell .bar { padding-inline: 28px; }
  .app-shell .main { padding: 0 28px 28px; }
  .app-shell .tabs { width: min(560px, calc(100% - 16px)); margin: 0 auto 10px; }

  .screen[data-screen="home"] {
    grid-template-columns: minmax(0, 360px) minmax(0, 1fr);
    align-items: start;
    column-gap: 24px;
  }
  /* Scoped to Home, so it outranks the phone's display: contents below it. */
  .screen[data-screen="home"] > .home-code,
  .screen[data-screen="home"] > .home-rest { display: grid; gap: 14px; align-content: start; min-width: 0; }
  .screen[data-screen="home"] > .home-code { position: sticky; top: 16px; }
  /* A switch between three lists is a switch, not a banner. */
  .app-shell .seg { width: min(480px, 100%); }

  .cards { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }

  .screen[data-screen="activity"], .screen[data-screen="profile"],
  .screen[data-screen="settings"], .screen[data-screen="archive"] {
    width: 100%;
    max-width: 680px;
    margin-inline: auto;
  }
  /* One card is one card: its stamps are drawn to its width, and at 680 each was a
     120px disc. It keeps about a phone's width, where the artboard drew it. */
  .screen[data-screen="card"] { width: 100%; max-width: 480px; margin-inline: auto; }
}

.bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: calc(18px + env(safe-area-inset-top)) 20px 14px;
}
.bar-eyebrow {
  font: 600 11px/1 var(--font-mono);
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0 0 6px;
}
.bar-title {
  font: 600 19px/1.1 var(--font-sans);
  letter-spacing: -0.02em;
  margin: 0;
  overflow-wrap: anywhere;
}
.code-btn {
  flex: none;
  height: 44px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: var(--grad-accent);
  color: var(--ink-deep);
  font: 600 13px/1 var(--font-sans);
  box-shadow: var(--cta-shadow);
  cursor: pointer;
  transition: transform 160ms var(--ease-out);
}
.code-btn:active { transform: scale(0.96); }

.bar-actions { display: flex; align-items: center; gap: 8px; flex: none; }
/* The artboard's Updates pill, with its unread count. It takes the accent only
   when there is something unread; the rest of the time it is a quiet control,
   because a permanent accent teaches people to stop seeing it. */
.updates-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--tint);
  color: var(--ink-78);
  font: 600 11px/1 var(--font-mono);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
}
.updates-btn[data-unread] { border-color: rgba(14, 116, 144, 0.4); background: rgba(14, 116, 144, 0.12); color: var(--accent); }
/* A TINT, NOT A FILL. Filled with the accent, the badge became the ground that
   the pill's own word sat over, and the pair measured 1:1 against each other.
   The count is the accent on its own tint, which reads at any size. */
.updates-count {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(14, 116, 144, 0.18);
  color: var(--accent);
  font: 600 11px/1 var(--font-mono);
}

.main { flex: 1; padding: 0 20px 28px; }

.banner {
  margin: 0 20px 12px;
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(14, 116, 144, 0.1);
  border: 1px solid rgba(14, 116, 144, 0.3);
  color: var(--accent);
  font: 500 12px/1.4 var(--font-mono);
}
.banner[data-bad] { background: rgba(179, 64, 42, 0.08); border-color: rgba(179, 64, 42, 0.3); color: var(--danger); }

/* Screens slide a little in the direction travelled. Short, because tabs are
   changed tens of times a day and a long move reads as the app being slow. */
.screen { display: grid; gap: 14px; animation: enter-r 220ms var(--ease-out) both; }
/* Home's two halves are one column on a phone (see the wide-screen rules below). */
.home-code, .home-rest { display: contents; }
.screen[data-dir="back"] { animation-name: enter-l; }
@keyframes enter-r { from { transform: translateX(14px); opacity: 0.55; } }
@keyframes enter-l { from { transform: translateX(-14px); opacity: 0.55; } }

/* --- the tab bar ------------------------------------------------------------ */
.tabs {
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  padding: 10px 10px calc(8px + env(safe-area-inset-bottom));
  border-radius: 24px 24px 0 0;
  border-top: 1px solid rgba(8, 40, 47, 0.18);
  background: linear-gradient(180deg, #ffffff, #f4f9fa);
  box-shadow: 0 -12px 28px -14px rgba(8, 40, 47, 0.315);
}
.tabs-rail {
  position: absolute;
  top: 0;
  left: 10px;
  /* Five tabs and four gaps. It was four and three, and the rail ran 86px past
     the right edge at the fifth tab, which the proof caught as a sideways
     scroll. */
  width: calc((100% - 20px - 16px) / 5);
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--accent), var(--fill));
  transform: translateX(calc(var(--i, 0) * (100% + 4px)));
  transition: transform 300ms var(--ease-out);
  pointer-events: none;
}
.tab {
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 6px;
  min-height: 52px;
  padding: 8px 2px 7px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: var(--ink-78);
  font: 500 12px/1 var(--font-sans);
  cursor: pointer;
}
.tab[aria-current="page"] {
  background: var(--grad-accent);
  color: var(--ink-deep);
  font-weight: 600;
  box-shadow: 0 8px 16px -9px rgba(14, 116, 144, 0.55);
}
.icon { display: inline-flex; line-height: 0; }

/* --- type and small parts --------------------------------------------------- */
.label {
  font: 600 11px/1.2 var(--font-mono);
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-76);
}
.label { margin: 0; }
.label[data-accent] { color: var(--accent); }
.label[data-danger] { color: var(--danger); }
.section-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin: 0 0 10px; }
/* 44px to press, with the same place in the line it had at 35: the extra
   height is taken back by the negative margin. */
.link-mono {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  border: 0;
  background: transparent;
  padding: 0;
  margin: -16px 0;
  font: 500 12px/1 var(--font-mono);
  color: var(--accent);
  cursor: pointer;
}
.display {
  font: 600 27px/1.12 var(--font-display);
  letter-spacing: -0.03em;
  margin: 0 0 10px;
  text-wrap: balance;
}
.lead { font: 400 14px/1.55 var(--font-sans); color: var(--ink-86); }
.fine { font: 400 12px/1.5 var(--font-sans); color: var(--ink-74); }
.fine a { color: var(--accent); text-underline-offset: 2px; }
.muted { color: var(--ink-76); font-size: 13px; line-height: 1.5; }
.error { color: var(--danger); font: 500 13px/1.45 var(--font-sans); }

.panel {
  padding: 15px 17px;
  border-radius: 18px;
  background: var(--tint-strong);
  border: 1px solid var(--line-strong);
}
.panel .label { margin: 0 0 9px; }
.panel-body { font: 400 13px/1.5 var(--font-sans); color: var(--ink-88); }
.panel-body + .panel-body { margin-top: 6px; }

.stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 9px; }
.stats[data-count="2"] { grid-template-columns: repeat(2, 1fr); }
.stat { padding: 12px 13px; border-radius: 16px; background: var(--tint-strong); border: 1px solid var(--line-strong); min-width: 0; }
.stat-v { font: 500 21px/1 var(--font-mono); margin: 0 0 6px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.stat-v[data-accent] { color: var(--accent); }
.stat-k { font: 600 11px/1.25 var(--font-mono); letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-76); }

.facts { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.facts { margin: 0; }
.fact { display: flex; flex-direction: column-reverse; gap: 6px; padding: 13px 14px; border-radius: 16px; background: var(--tint); border: 1px solid var(--line); min-width: 0; }
.fact-v { font: 500 16px/1.1 var(--font-mono); margin: 0; overflow-wrap: anywhere; }
.fact-k { font: 600 11px/1.25 var(--font-mono); letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-74); }

/* --- buttons ---------------------------------------------------------------- */
.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  height: 52px;
  padding: 0 18px;
  border: 0;
  border-radius: 16px;
  font: 600 15px/1 var(--font-sans);
  cursor: pointer;
  transition: transform 160ms var(--ease-out);
  text-decoration: none;
}
.btn:active:not(:disabled) { transform: scale(0.97); }
.btn-primary { background: var(--grad-accent); color: var(--ink-deep); box-shadow: var(--cta-shadow); }
.btn-primary:disabled { background: rgba(8, 40, 47, 0.1); color: var(--ink-74); box-shadow: none; cursor: default; }
.btn-secondary { background: var(--tint-strong); color: var(--ink); border: 1px solid rgba(8, 40, 47, 0.28); font-size: 14px; }
.btn-quiet { height: 44px; background: transparent; color: var(--ink-74); font-size: 13px; }
.btn-danger { background: linear-gradient(160deg, #c2492f, #8f2f1e); color: #ffffff; }
.btn-danger:disabled { opacity: 0.6; }
.btn-outline-danger { height: 48px; border-radius: 14px; background: transparent; border: 1px solid rgba(179, 64, 42, 0.29); color: var(--danger); font-size: 13px; }
.btn-inline { width: auto; justify-self: start; }

/* --- the mark --------------------------------------------------------------- */
.brand { display: flex; align-items: center; gap: 11px; margin: 6px 0 8px; }
.mark {
  position: relative;
  display: block;
  flex: none;
  width: 34px;
  height: 34px;
  background: var(--seal);
  border-radius: 9px 9px 9px 2px;
  overflow: hidden;
}
.mark::before, .mark::after { content: ""; position: absolute; top: 9px; height: 17px; background: var(--ink); }
.mark::before { left: 10px; width: 11px; border-radius: 0 999px 999px 0; }
.mark::after { left: 7px; width: 3px; }
.brand-word { font: 600 15px/1 var(--font-sans); letter-spacing: -0.025em; }

/* --- the code --------------------------------------------------------------- */
.code-panel {
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 22px;
  background: var(--shell);
  box-shadow: var(--shell-shadow);
}
.digits { display: flex; gap: 7px; align-items: stretch; }
.digit {
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 17px 0;
  border-radius: 14px;
  background: var(--grad-accent);
  color: var(--ink-deep);
  font: 500 27px/1 var(--font-mono);
  font-variant-numeric: tabular-nums;
  /* The artboard's inset was amber and near-black, left from the clay
     register. The same depth, in the teal family. */
  box-shadow: 0 10px 20px -8px rgba(8, 40, 47, 0.27), inset 0 -3px 6px rgba(8, 40, 47, 0.12), inset 0 3px 5px rgba(255, 255, 255, 0.45);
}
.digits-gap { flex: none; width: 5px; }
@media (max-width: 350px) { .digit { font-size: 23px; padding: 15px 0; } .digits { gap: 5px; } }
.code-block .code-meta { margin-top: 14px; }
.code-panel .section-head { margin: 0; }
.code-meta { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.timer { flex: 1; height: 6px; border-radius: 999px; background: rgba(8, 40, 47, 0.12); overflow: hidden; }
.timer-bar {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--accent), var(--fill));
  transform-origin: left center;
  transform: scaleX(var(--fraction, 1));
  transition: transform 1s linear;
}
.timer-text { flex: none; font: 500 12px/1 var(--font-mono); color: var(--ink-78); font-variant-numeric: tabular-nums; }
.notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(14, 116, 144, 0.1);
  border: 1px solid rgba(14, 116, 144, 0.3);
  color: var(--accent);
  font: 500 12px/1.45 var(--font-mono);
}
.notice::before { content: ""; flex: none; width: 8px; height: 8px; margin-top: 4px; border-radius: 999px; background: var(--accent); }

/* --- the due reward --------------------------------------------------------- */
.due {
  position: relative;
  overflow: hidden;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 15px 17px;
  border: 0;
  border-radius: 20px;
  text-align: start;
  background: linear-gradient(155deg, #5eead4, #4dd6e8 52%, #38bdf8);
  box-shadow: 0 8px 18px -12px rgba(8, 40, 47, 0.34), inset 0 0 0 1px color-mix(in oklch, #0b5c74 22%, #ffffff);
  color: var(--ink-deep);
  cursor: pointer;
  animation: breathe 4.5s ease-in-out infinite;
}
.due::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 38%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0));
  animation: shimmer 3.6s ease-in-out infinite;
  pointer-events: none;
}
.due-reward { display: block; font: 600 15px/1.2 var(--font-sans); margin: 6px 0 4px; }
.due-shop { font: 500 12px/1.2 var(--font-mono); color: rgba(4, 40, 46, 0.8); }
.due .label { color: rgba(4, 40, 46, 0.8); }
.due-arrow { font: 600 20px/1 var(--font-sans); }
@keyframes breathe { 50% { transform: scale(1.02); } }
@keyframes shimmer { from { transform: translateX(-130%) skewX(-16deg); } to { transform: translateX(260%) skewX(-16deg); } }

/* --- onboarding ------------------------------------------------------------- */
.onb { padding: 17px 18px 18px; border-radius: 20px; background: var(--shell); box-shadow: var(--shell-shadow); }
.onb-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin: 0 0 12px; }
.onb-count { font: 600 12px/1 var(--font-mono); color: var(--ink-78); white-space: nowrap; }
.onb-track { height: 6px; border-radius: 999px; background: rgba(8, 40, 47, 0.12); overflow: hidden; margin: 0 0 15px; }
.onb-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--accent), var(--fill));
  transform-origin: left center;
  transform: scaleX(var(--pct, 0));
  transition: transform 720ms var(--ease-out);
}
.onb-steps { display: grid; gap: 8px; list-style: none; margin: 0; padding: 0; }
.onb-step {
  width: 100%;
  display: grid;
  grid-template-columns: 26px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid var(--line-strong);
  background: var(--tint);
  text-align: start;
  cursor: pointer;
}
.onb-step[data-done] { border-color: rgba(15, 122, 82, 0.28); background: rgba(15, 122, 82, 0.08); cursor: default; }
.onb-badge {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 40, 47, 0.12);
  color: var(--ink-86);
  font: 600 12px/1 var(--font-mono);
}
.onb-step[data-done] .onb-badge { background: var(--ok); color: #ffffff; }
.onb-title { display: block; font: 500 14px/1.25 var(--font-sans); margin: 0 0 4px; }
.onb-body { font: 400 12px/1.4 var(--font-sans); color: var(--ink-74); }
.onb-tag { font: 600 11px/1 var(--font-mono); letter-spacing: 0.06em; text-transform: uppercase; color: var(--accent); white-space: nowrap; }
.onb-step[data-done] .onb-tag { color: var(--ok); }

/* --- cards ------------------------------------------------------------------ */
.cards { display: grid; gap: 12px; list-style: none; margin: 0; padding: 0; }
.shrink { min-width: 0; }
.card {
  --hue: #0b5c74;
  position: relative;
  width: 100%;
  display: grid;
  gap: 12px;
  padding: 16px 17px;
  border: 0;
  border-radius: 20px;
  text-align: start;
  color: var(--ink);
  background: #e9f1f3;
  background: linear-gradient(155deg, color-mix(in oklch, var(--hue) 10%, #ffffff), color-mix(in oklch, var(--hue) 20%, #ffffff));
  box-shadow: 0 9px 19px -12px rgba(8, 40, 47, 0.34), inset 0 0 0 1px color-mix(in oklch, var(--hue) 22%, #ffffff);
  cursor: pointer;
  transition: transform 160ms var(--ease-out);
}
.card:has(.card-open:active) { transform: scale(0.98); }
/* The name is the control that opens the card, and its ::after stretches over
   the whole card so the target is the card. The heart sits above that layer,
   which is why the card itself is no longer a button: a button cannot hold one. */
.card-open {
  display: block;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0 0 5px;
  text-align: start;
  font: 600 15px/1.2 var(--font-sans);
  color: var(--ink);
  cursor: pointer;
  overflow-wrap: anywhere;
}
.card-open::after { content: ""; position: absolute; inset: 0; border-radius: 20px; }
.card-marks { display: flex; align-items: center; gap: 8px; flex: none; position: relative; z-index: 1; }
.heart {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -11px -11px -11px 0;
  border: 0;
  background: transparent;
  color: var(--ink-74);
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
  transition: transform 260ms var(--ease-out), color 200ms var(--ease-out);
}
.heart[data-on] { color: var(--seal); transform: scale(1.1); }
.card[data-closing] { --hue: #5b6b70; }
.card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.card-name { font: 600 15px/1.2 var(--font-sans); margin: 0 0 5px; overflow-wrap: anywhere; }
.card-sub { font: 500 12px/1.35 var(--font-mono); color: var(--ink-78); }
.card-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; font: 500 12px/1.2 var(--font-mono); color: var(--ink-78); }
.card-foot > :last-child { font-size: 12px; text-align: end; }
.chip {
  flex: none;
  padding: 6px 9px;
  border-radius: 999px;
  background: rgba(8, 40, 47, 0.1);
  color: var(--ink);
  font: 600 11px/1 var(--font-mono);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
}
.chip[data-ready] { background: var(--grad-accent); color: var(--ink-deep); }
.pips { display: flex; gap: 5px; }
.pip { flex: 1; height: 6px; border-radius: 999px; background: rgba(8, 40, 47, 0.16); }
.pip[data-on] { background: var(--hue); }
.bar-track { height: 8px; border-radius: 999px; background: rgba(8, 40, 47, 0.135); overflow: hidden; }
.bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #08282f, #3f5c65);
  transform-origin: left center;
  transform: scaleX(var(--pct, 0));
  transition: transform 900ms var(--ease-out);
}
.closing {
  display: grid;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(8, 40, 47, 0.07);
  font: 400 12px/1.45 var(--font-sans);
  color: var(--ink-88);
}

/* --- the card, opened ------------------------------------------------------- */
.back {
  justify-self: start;
  height: 44px;
  padding: 0 6px;
  margin: -6px -6px -6px;
  border: 0;
  background: transparent;
  font: 500 12px/1 var(--font-mono);
  letter-spacing: 0.08em;
  color: var(--ink-74);
  cursor: pointer;
}
.hero {
  --hue: #0b5c74;
  padding: 20px;
  border-radius: 24px;
  background: #e9f1f3;
  background: linear-gradient(155deg, color-mix(in oklch, var(--hue) 10%, #ffffff), color-mix(in oklch, var(--hue) 20%, #ffffff));
  box-shadow: 0 9px 19px -12px rgba(8, 40, 47, 0.34), inset 0 0 0 1px color-mix(in oklch, var(--hue) 22%, #ffffff);
}
.hero-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin: 0 0 18px; }
.hero-name { font: 600 11px/1.3 var(--font-mono); letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-78); margin: 0 0 7px; }
.hero-reward { font: 600 18px/1.25 var(--font-sans); letter-spacing: -0.02em; margin: 0; overflow-wrap: anywhere; }
.stamps { display: grid; grid-template-columns: repeat(5, 1fr); gap: 9px; margin: 0 0 16px; list-style: none; padding: 0; }
.stamp {
  aspect-ratio: 1;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 40, 47, 0.16);
  color: var(--ink-74);
  font: 600 12px/1 var(--font-mono);
  box-shadow: inset -2px -2px 5px rgba(8, 40, 47, 0.1), inset 2px 2px 5px rgba(255, 255, 255, 0.2);
}
.stamp[data-on] {
  background: linear-gradient(155deg, #0b5c74, #08414f);
  color: #ffffff;
  box-shadow: 0 6px 12px -5px rgba(8, 40, 47, 0.225), inset 0 -2px 4px rgba(8, 40, 47, 0.144), inset 0 2px 3px rgba(255, 255, 255, 0.3);
}
.stamp[data-landed] { animation: land 480ms var(--ease-out); }
@keyframes land { 0% { transform: scale(1.18); } 100% { transform: scale(1); } }
.points { margin: 0 0 16px; }
.points-big { display: flex; align-items: baseline; gap: 9px; margin: 0 0 12px; }
.points-big b { font: 500 38px/1 var(--font-mono); font-variant-numeric: tabular-nums; }
.points-big span { font: 600 11px/1 var(--font-mono); letter-spacing: 0.07em; text-transform: uppercase; color: var(--ink-78); }
.hero-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 14px;
  border-top: 1px dashed rgba(8, 40, 47, 0.18);
}
.hero-progress { font: 600 12px/1.2 var(--font-mono); color: var(--ink-78); }
.hero-code { font: 600 15px/1 var(--font-mono); letter-spacing: 0.11em; font-variant-numeric: tabular-nums; }
.hero-code small { display: block; font: 600 11px/1 var(--font-mono); letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-74); margin: 0 0 5px; text-align: end; }
.hero-code .hero-code-same { margin: 6px 0 0; font: 500 12px/1.3 var(--font-sans); letter-spacing: 0; text-transform: none; max-width: 17ch; margin-inline-start: auto; }
.rung { display: grid; grid-template-columns: 26px 1fr; gap: 11px; align-items: center; }
.rung-badge {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--fill);
  color: var(--ink-deep);
  font: 600 11px/1 var(--font-mono);
}
.rung-title { font: 500 14px/1.25 var(--font-sans); margin: 0 0 3px; }
.rung-meta { font: 500 12px/1.4 var(--font-mono); color: var(--ink-76); }

/* --- rows: recent and activity ---------------------------------------------- */
.rows { display: grid; gap: 8px; list-style: none; margin: 0; padding: 0; }
.row {
  display: grid;
  grid-template-columns: 34px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-radius: 16px;
  background: var(--tint);
  border: 1px solid var(--line);
}
.dot {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(14, 116, 144, 0.16);
  color: var(--accent);
  font: 600 13px/1 var(--font-mono);
}
/* The artboard's reward dot was a brown ink from the old register. */
.dot[data-kind="reward"] { background: rgba(94, 234, 212, 0.4); color: var(--ink-deep); }
.dot[data-kind="back"] { background: rgba(179, 64, 42, 0.1); color: var(--danger); }
.dot[data-kind="quiet"] { background: rgba(8, 40, 47, 0.1); color: var(--ink-86); }
.row-main { min-width: 0; }
.row-title { font: 500 14px/1.3 var(--font-sans); margin: 0 0 4px; }
.row-meta { font: 500 12px/1.3 var(--font-mono); color: var(--ink-76); overflow-wrap: anywhere; }
.row-detail { font: 400 12px/1.45 var(--font-sans); color: var(--ink-86); margin: 5px 0 0; }
.row-value { font: 600 12px/1 var(--font-mono); color: var(--ink-86); white-space: nowrap; }
.row[data-back] .row-value { color: var(--danger); }
.group-title { margin: 6px 0 0; }

/* --- settings --------------------------------------------------------------- */
/* Profile's standing card: the artboard's one filled panel on that screen. */
.standing { display: grid; gap: 8px; padding: 16px 17px; border-radius: 20px; background: var(--grad-accent); color: var(--ink-deep); }
.standing .label { color: rgba(4, 40, 46, 0.78); }
.standing-line { font: 600 16px/1.3 var(--font-sans); }
.standing-body { font: 400 13px/1.5 var(--font-sans); color: rgba(4, 40, 46, 0.82); }

.identity { display: flex; align-items: center; gap: 13px; padding: 18px; border-radius: 20px; background: var(--shell); box-shadow: var(--shell-shadow); }
.avatar {
  flex: none;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(150deg, #5eead4, #38bdf8);
  color: var(--ink-deep);
  font: 600 16px/1 var(--font-mono);
}
.identity-name { font: 600 16px/1.2 var(--font-sans); margin: 0 0 5px; overflow-wrap: anywhere; }
.identity-sub { font: 500 12px/1.2 var(--font-mono); color: var(--ink-74); overflow-wrap: anywhere; }
.group { border-radius: 18px; background: var(--tint-strong); border: 1px solid var(--line-strong); overflow: hidden; }
.group > .label { display: block; padding: 15px 17px 11px; }
.group-row {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  min-height: 58px;
  padding: 14px 17px;
  border: 0;
  border-top: 1px solid rgba(8, 40, 47, 0.12);
  background: transparent;
  text-align: start;
  cursor: pointer;
}
.group-row:disabled { cursor: default; }
.group-row-title { display: block; font: 500 14px/1.25 var(--font-sans); margin: 0 0 4px; }
.group-row-title[data-danger] { color: var(--danger); }
.group-row-body { font: 400 12px/1.4 var(--font-sans); color: var(--ink-76); overflow-wrap: anywhere; }
.group-row-body[data-warn] { color: #845009; }
.group-row-end { display: flex; align-items: center; gap: 8px; color: var(--ink-74); font: 500 12px/1 var(--font-mono); }

/* --- empty states ----------------------------------------------------------- */
.empty { padding: 34px 22px; text-align: center; border-radius: 20px; background: rgba(8, 40, 47, 0.05); border: 1px solid var(--line); display: grid; gap: 8px; justify-items: center; }
.empty-title { font: 600 16px/1.35 var(--font-sans); }
.empty-body { font: 400 13px/1.55 var(--font-sans); color: var(--ink-78); max-width: 32ch; }
.empty .btn { margin-top: 10px; width: auto; }

/* --- sign in ---------------------------------------------------------------- */
.auth { display: grid; gap: 16px; padding: calc(24px + env(safe-area-inset-top)) 22px calc(28px + env(safe-area-inset-bottom)); align-content: start; flex: 1; }
.seg { display: flex; gap: 6px; padding: 4px; border-radius: 14px; background: var(--tint-strong); border: 1px solid var(--line-strong); }
.seg button { flex: 1; height: 44px; border: 0; border-radius: 12px; background: transparent; color: var(--ink-78); font: 600 13px/1 var(--font-sans); cursor: pointer; }
.seg button[aria-selected="true"] { background: var(--fill); color: var(--ink-deep); }
.stack { display: grid; gap: 9px; }
.provider {
  height: 52px;
  display: grid;
  grid-template-columns: 22px 1fr 22px;
  gap: 12px;
  align-items: center;
  padding: 0 16px;
  border-radius: 16px;
  border: 1px solid rgba(8, 40, 47, 0.28);
  background: var(--tint-strong);
  color: var(--ink);
  font: 600 14px/1 var(--font-sans);
  text-align: start;
  cursor: pointer;
  transition: transform 160ms var(--ease-out);
}
.provider:active { transform: scale(0.98); }
.provider[data-provider="apple"] { background: var(--ink); border-color: var(--ink); color: #ffffff; }
.provider-mark { display: flex; justify-content: center; font: 600 15px/1 var(--font-sans); color: var(--accent); }
.provider-mark svg { width: 18px; height: 18px; }
.provider[data-provider="apple"] .provider-mark { color: #ffffff; }
.or { display: flex; align-items: center; gap: 12px; }
.or::before, .or::after { content: ""; flex: 1; height: 1px; background: var(--line); }
.or span { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-76); }
.promises { display: grid; gap: 9px; margin: 0; padding: 14px 16px; list-style: none; border-radius: 16px; background: var(--tint); border: 1px solid var(--line); }
.promises li { display: grid; grid-template-columns: 14px 1fr; gap: 11px; font: 400 13px/1.45 var(--font-sans); color: var(--ink-86); }
.promises li::before { content: ""; width: 6px; height: 6px; margin-top: 6px; border-radius: 999px; background: var(--accent); }
.field { display: grid; gap: 7px; }
.field-label { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-76); }
.input {
  width: 100%;
  height: 50px;
  padding: 0 15px;
  border-radius: 14px;
  border: 1px solid var(--line-input);
  background: rgba(8, 40, 47, 0.05);
  color: var(--ink);
  font: 400 16px/1 var(--font-sans);
  outline: 0;
}
.input::placeholder { color: rgba(8, 40, 47, 0.6); }
.input:focus-visible { border-color: var(--seal); box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.2); outline: 0; }
.input[aria-invalid="true"] { border-color: rgba(179, 64, 42, 0.5); }
.input-group { display: flex; gap: 8px; }
.input-prefix { flex: none; display: flex; align-items: center; padding: 0 13px; height: 50px; border-radius: 14px; border: 1px solid var(--line-input); background: rgba(8, 40, 47, 0.05); font: 500 15px/1 var(--font-mono); }
.input-mono { font: 500 16px/1 var(--font-mono); letter-spacing: 0.06em; }
/* Six cells over one real input. The input takes the typing, the paste and
   the one-time-code autofill; the cells are what the artboard draws. The
   artboard's on-screen keypad is not built: it would hide the phone's own
   autofill of the code, which is faster than any keypad. */
.otp { position: relative; display: flex; gap: 8px; }
.otp-cell {
  flex: 1;
  text-align: center;
  padding: 16px 0;
  border-radius: 14px;
  border: 1px solid rgba(8, 40, 47, 0.24);
  background: rgba(8, 40, 47, 0.06);
  font: 500 21px/1 var(--font-mono);
  min-height: 55px;
}
.otp-cell[data-filled] { background: var(--grad-accent); border-color: transparent; color: var(--ink-deep); }
.otp-cell[data-next] { border: 2px solid var(--accent); }
.otp-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: 0;
  font-size: 16px;
  color: transparent;
  caret-color: transparent;
}
.otp:focus-within .otp-cell[data-next] { box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.2); }

/* --- sheets ----------------------------------------------------------------- */
.scrim {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(8, 40, 47, 0.28);
  animation: fade 200ms var(--ease-out) both;
}
.sheet {
  width: 100%;
  max-width: 440px;
  max-height: 88dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 22px 20px calc(24px + env(safe-area-inset-bottom));
  border-radius: 26px 26px 0 0;
  background: linear-gradient(168deg, #f7fafb, #ffffff);
  border-top: 1px solid rgba(8, 40, 47, 0.24);
  box-shadow: 0 -20px 44px -14px rgba(8, 40, 47, 0.36);
  animation: sheet-in 320ms var(--ease-drawer) both;
}
.sheet:focus { outline: 0; }
.grabber { display: block; width: 44px; height: 4px; margin: 0 auto 18px; border-radius: 999px; background: var(--line); }
.sheet .label { margin: 0 0 10px; }
.sheet-title { font: 600 20px/1.2 var(--font-sans); letter-spacing: -0.02em; margin: 0 0 12px; overflow-wrap: anywhere; }
.sheet-body { font: 400 14px/1.55 var(--font-sans); color: var(--ink-86); margin: 0 0 18px; }
.sheet-list { display: grid; gap: 10px; margin: 0 0 18px; padding: 0; list-style: none; }
.sheet-list li { display: grid; grid-template-columns: 14px 1fr; gap: 10px; font: 400 14px/1.5 var(--font-sans); color: var(--ink-86); }
.sheet-list li::before { content: ""; width: 6px; height: 6px; margin-top: 8px; border-radius: 999px; background: var(--accent); }
.sheet-facts { display: grid; gap: 10px; margin: 0 0 18px; padding: 13px 15px; border-radius: 16px; background: var(--tint); border: 1px solid var(--line-strong); }
.sheet-fact { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.sheet-fact span:first-child { font: 400 13px/1.3 var(--font-sans); color: var(--ink-78); }
.sheet-fact span:last-child { font: 500 13px/1.3 var(--font-mono); text-align: end; overflow-wrap: anywhere; }
.sheet-actions { display: grid; gap: 9px; }
.sheet .error { margin: 0 0 14px; }
.sheet-block .error { margin: 0; }
.sheet-block { display: grid; gap: 14px; margin: 0 0 18px; }
@keyframes fade { from { opacity: 0; } }
@keyframes sheet-in { from { transform: translateY(100%); } }

/* --- the toast -------------------------------------------------------------- */
.toast {
  position: fixed;
  left: 50%;
  bottom: calc(100px + env(safe-area-inset-bottom));
  z-index: 50;
  width: min(400px, calc(100% - 40px));
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff, #f4f9fa);
  border: 1px solid rgba(8, 40, 47, 0.26);
  box-shadow: 0 20px 38px -14px rgba(8, 40, 47, 0.36);
  font: 500 13px/1.4 var(--font-sans);
  transform: translate(-50%, 0);
  opacity: 1;
  transition: transform 300ms var(--ease-out), opacity 300ms var(--ease-out);
}
.toast[data-entering] { transform: translate(-50%, 14px); opacity: 0; }
.toast::before { content: ""; flex: none; width: 8px; height: 8px; border-radius: 999px; background: var(--accent); }

/* --- the seal: a real event only -------------------------------------------- */
.seal-layer { position: fixed; inset: 0; z-index: 70; pointer-events: none; background: rgba(8, 40, 47, 0.1); animation: fade 200ms var(--ease-out) both; }
.seal-ring, .seal {
  position: absolute;
  top: 44%;
  left: 50%;
  border-radius: 999px;
}
.seal-ring { width: 150px; height: 150px; border: 3px solid rgba(14, 116, 144, 0.7); animation: ring 900ms var(--ease-out) both; }
.seal {
  width: 132px;
  height: 132px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: linear-gradient(150deg, #0b5c74, #0e7490 58%, #11525e);
  box-shadow: 0 20px 38px -10px rgba(14, 116, 144, 0.7), inset 0 -5px 10px rgba(8, 40, 47, 0.22), inset 0 5px 8px rgba(255, 255, 255, 0.18);
  animation: seal 1150ms var(--ease-out) both;
}
/* White on the seal. The artboard's dark ink on this teal measured well under
   AA; it was the clay seal's ink, left behind. */
.seal b { font: 600 28px/1 var(--font-display); color: #ffffff; }
.seal span { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: rgba(255, 255, 255, 0.92); }
@keyframes seal {
  0% { transform: translate(-50%, -50%) scale(2.1) rotate(-14deg); opacity: 0; }
  38% { transform: translate(-50%, -50%) scale(1) rotate(0); opacity: 1; }
  52% { transform: translate(-50%, -50%) scale(0.95); }
  66% { transform: translate(-50%, -50%) scale(1); }
  100% { transform: translate(-50%, -50%) scale(0.9) rotate(5deg); opacity: 0; }
}
@keyframes ring {
  0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.85; }
  100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
}

/* --- the intro: signed out only --------------------------------------------- */
.intro {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  background: linear-gradient(168deg, #ffffff, #ffffff 52%, #f4f9fa);
  pointer-events: none;
  animation: intro-out 1500ms var(--ease-out) both;
}
.intro-mark { position: relative; width: 96px; height: 96px; display: flex; align-items: center; justify-content: center; }
.intro-ring { position: absolute; inset: 0; border-radius: 999px; border: 2px solid rgba(14, 116, 144, 0.72); animation: intro-ring 1500ms var(--ease-out) both; }
.intro .mark { width: 76px; height: 76px; background: var(--ink); border-radius: 20px 20px 20px 4px; animation: intro-seal 1500ms var(--ease-out) both; }
.intro .mark::before, .intro .mark::after { top: 19px; height: 39px; background: var(--ground); }
.intro .mark::before { left: 22px; width: 25px; }
.intro .mark::after { left: 15px; width: 7px; }
.intro-word { font: 600 24px/1 var(--font-display); letter-spacing: 0.09em; animation: intro-word 1500ms var(--ease-out) both; }
@keyframes intro-seal {
  0% { transform: translateY(-120px) scale(2.2) rotate(-16deg); opacity: 0; }
  40% { transform: translateY(0) scale(1) rotate(0); opacity: 1; }
  54% { transform: translateY(4px) scale(0.93); }
  68% { transform: translateY(0) scale(1); }
  100% { transform: translateY(-40px) scale(0.9) rotate(6deg); opacity: 0; }
}
@keyframes intro-ring { 0%, 32% { transform: scale(0.4); opacity: 0; } 46% { transform: scale(1); opacity: 0.9; } 100% { transform: scale(2.6); opacity: 0; } }
@keyframes intro-word { 0%, 34% { opacity: 0; letter-spacing: 0.4em; } 58%, 100% { opacity: 1; letter-spacing: -0.025em; } }
@keyframes intro-out { 0%, 74% { opacity: 1; } 100% { opacity: 0; visibility: hidden; } }

/* --- motion, reduced -------------------------------------------------------- */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 1ms !important;
  }
  .intro, .seal-layer, .due::before { display: none; }
}

.visually-hidden {
  position: absolute !important;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
`;
